// Full bilingual content dictionary for Alexa Financial Multiservices
export const translations = {
  en: {
    brand: { name: "Alexa Financial", suffix: "Multiservices" },
    nav: {
      services: "Services", process: "Process", websites: "Websites",
      pricing: "Pricing", about: "About", faq: "FAQ",
      cta: "Book a Call",
      blog: "Blog",
    },
    hero: {
      badge: "Full-service business & financial partner",
      title1: "Building Businesses",
      title2: "Ready to Grow.",
      subtitle: "We help entrepreneurs create LLCs, obtain EINs, build business credit, access funding, design professional websites, repair credit, and grow their businesses with confidence.",
      primary: "Book a Consultation",
      secondary: "Explore Services",
      scroll: "Scroll",
      trust: "Trusted by 2,500+ entrepreneurs",
      based: "Maryland & Nationwide",
      captionRole: "Founder & CEO",
    },
    marquee: ["LLC FORMATION", "EIN REGISTRATION", "BUSINESS FUNDING", "BUSINESS CREDIT", "CREDIT REPAIR", "WEBSITE DESIGN", "CONSULTING"],
    stats: {
      tagline: "Your strategic partner to create, fund, and grow your business.",
      items: [
        { value: 2500, suffix: "+", label: "Businesses Launched" },
        { value: 48, suffix: "M+", prefix: "$", label: "Funding Secured" },
        { value: 98, suffix: "%", label: "Client Satisfaction" },
      ],
    },
    services: {
      eyebrow: "What We Do",
      title1: "Everything your business needs,",
      title2: "under one roof.",
      subtitle: "From formation to funding to your online presence — delivered with the precision of a private financial firm in Maryland and nationwide.",
      items: [
        { tag: "Foundation", title: "LLC Formation", desc: "State filing, registered agent and operating agreements — your business, legally structured for growth." },
        { tag: "Compliance", title: "EIN Registration", desc: "Federal Tax ID secured fast so you can open bank accounts, hire, and operate with confidence." },
        { tag: "Capital", title: "Business Funding", desc: "Access capital through lender-ready profiles and strategic funding aligned to your growth plan." },
        { tag: "Leverage", title: "Business Credit", desc: "Build tradelines and a strong business credit profile to borrow without personal guarantees." },
        { tag: "Restore", title: "Credit Repair", desc: "Dispute, remove and rebuild — restore your personal credit with a proven compliance framework." },
        { tag: "Presence", title: "Professional Website Design", desc: "Conversion-focused, custom websites that make your brand look like a market leader." },
        { tag: "Launch", title: "One-Page Websites", desc: "A fast, elegant landing page to get your business online and generating leads today." },
        { tag: "Growth", title: "Business Consulting", desc: "1-on-1 advisory to map your roadmap from formation to scale with confidence." },
      ],
    },
    manifesto: {
      eyebrow: "Our Philosophy",
      title1: "The principles that",
      title2: "build empires.",
      items: [
        { n: "01", title: "Structure before scale", body: "We architect the legal and financial foundation so your ambition never outpaces your infrastructure." },
        { n: "02", title: "Capital with intention", body: "Funding is a tool, not a trophy. We position you for the right capital at the right cost — and the right time." },
        { n: "03", title: "Credit as leverage", body: "Business credit separates you from your business, protects your assets and multiplies what you can build." },
      ],
    },
    timeline: {
      eyebrow: "The Process",
      title1: "From idea to",
      title2: "funded & growing.",
      steps: [
        { step: "01", title: "Discovery Call", desc: "We map your goals, industry and timeline in a private strategy session." },
        { step: "02", title: "Entity Formation", desc: "LLC filed, EIN secured, compliance documents delivered." },
        { step: "03", title: "Credit Foundation", desc: "Business credit profile established with lender-ready tradelines." },
        { step: "04", title: "Funding Access", desc: "We position and submit for capital aligned to your growth plan." },
        { step: "05", title: "Scale & Advisory", desc: "Ongoing consulting and web presence to grow with confidence." },
      ],
      stepLabel: "Step",
    },
    portfolio: {
      eyebrow: "Selected Work",
      title1: "Businesses we helped",
      title2: "build.",
      items: [
        { cat: "LLC + Funding", title: "Meridian Holdings" },
        { cat: "Website Design", title: "Atlas Web Presence" },
        { cat: "Business Credit", title: "Vantage Credit Build" },
        { cat: "Consulting", title: "Noble & Co. Advisory" },
      ],
    },
    testimonials: {
      eyebrow: "Client Stories",
      title1: "Trusted by",
      title2: "founders.",
      items: [
        { name: "Marcus R.", role: "Founder, Logistics Company", quote: "Alexa Financial took me from an idea to a funded LLC in weeks. The process felt like working with a private wealth firm." },
        { name: "Danielle C.", role: "CEO, Beauty Brand", quote: "I secured business credit without a personal guarantee. Their strategy is genuinely next level." },
        { name: "Andre W.", role: "Owner, Real Estate Firm", quote: "The team rebuilt my credit and structured my business flawlessly. I finally feel in control of my finances." },
        { name: "Sofia M.", role: "Founder, Design Studio", quote: "From branding to funding, they handled everything. My business looks and operates like a Fortune 500." },
      ],
    },
    pricing: {
      eyebrow: "LLC Packages",
      title1: "Launch your business",
      title2: "the right way.",
      subtitle: "Transparent, all-inclusive LLC formation packages. State filing fee included in every plan.",
      oneTime: "one-time",
      plans: [
        {
          name: "Entrepreneur Plan", price: "$399.99", tagline: "Everything you need to form your LLC.",
          features: ["LLC Registration", "EIN (Federal Tax ID)", "Operating Agreement", "State Filing Fee Included", "Personalized Business Consultation"],
        },
        {
          name: "Premium Plan", price: "$599.99", tagline: "Formation plus your brand & credit foundation.",
          features: ["Everything in the Entrepreneur Plan", "Professional One-Page Website", "D-U-N-S\u00AE Profile Setup", "Initial Business Credit Profile Review", "One Professional Social Media Flyer", "Digital Business Card", "Facebook Banner", "WhatsApp Business Cover", "State Filing Fee Included"],
        },
      ],
    },
    websites: {
      eyebrow: "Professional Website Design",
      title1: "A website that",
      title2: "wins clients.",
      subtitle: "Custom-built websites designed to build trust and convert visitors into customers.",
      startingAt: "Starting at",
      packages: [
        {
          name: "Landing Page", price: "$299.99", tagline: "Perfect for new businesses that want a professional online presence.",
          features: ["Custom Landing Page", "Mobile Responsive Design", "Contact Form", "WhatsApp Integration", "Basic SEO", "Social Media Links"],
        },
        {
          name: "Business Website", price: "$499.99", tagline: "Perfect for businesses that want to build credibility and attract more customers.",
          features: ["3\u20135 Custom Pages", "Unique Professional Design", "Mobile Responsive", "Basic SEO", "Google Maps Integration", "Contact Forms", "Social Media Integration"],
        },
        {
          name: "Premium Website", price: "$799.99", starting: true, tagline: "Perfect for businesses that want to sell online and compete with larger companies.",
          features: ["More than 5 Pages", "Online Store (E-commerce)", "Payment Gateway Integration", "Advanced Animations", "Advanced SEO", "Premium Custom Design", "Optional Blog", "Website Training"],
        },
      ],
      note: "Every website is custom-designed for your business. No templates. No copy-and-paste. Your business deserves a unique online presence that builds trust and attracts more customers.",
      getStarted: "Get Started",
      contactUs: "Contact Us",
    },
    about: {
      eyebrow: "Meet Your Advisor",
      title1: "Guided by",
      title2: "Alexa Guzman.",
      name: "Alexa Guzman",
      role: "Founder & CEO, Alexa Financial Multiservices",
      bio: "Alexa founded Alexa Financial Multiservices to give entrepreneurs the same advantages the wealthy have long enjoyed — proper structure, real funding, and a brand that commands trust. Based in Maryland and serving clients nationwide, she personally guides every business from formation to growth.",
      stat1: "Businesses guided", stat1v: "2,500+",
      stat2: "Client satisfaction", stat2v: "98%",
    },
    faq: {
      eyebrow: "Questions",
      title1: "Answers,",
      title2: "clearly.",
      items: [
        { q: "How long does it take to form my LLC?", a: "Most entities are filed within 24\u201372 hours depending on your state. Expedited options are available for select states, including Maryland." },
        { q: "Can I really get funding without a personal guarantee?", a: "Yes. Once your business credit profile and tradelines are established, we position you for financing that relies on your business \u2014 not your personal credit." },
        { q: "Do you work with clients outside of Maryland?", a: "Absolutely. We are based in Maryland and serve entrepreneurs nationwide, tailoring filings and strategy to your specific state." },
        { q: "Is website design included in the LLC plans?", a: "The Premium LLC Plan includes a professional one-page website. Full multi-page and e-commerce websites are offered separately under our Website Design packages." },
        { q: "Is my information secure?", a: "Yes. We use strict confidentiality protocols and secure systems to protect your data at every step." },
      ],
    },
    booking: {
      eyebrow: "Book a Consultation",
      title1: "Reserve your private",
      title2: "strategy call.",
      subtitle: "Pick a date and time that works for you. A dedicated Alexa Financial advisor will confirm and send your call link.",
      selectDate: "Select a date",
      selectDateFirst: "Select a date first",
      availableTimes: "Available times",
      chooseDate: "Choose a date to see available time slots.",
      loading: "Loading slots...",
      name: "Full name", email: "Email address", phone: "Phone (optional)",
      service: "Interested service", notes: "Anything we should know? (optional)",
      general: "General Consultation",
      confirm: "Confirm Booking", booking: "Booking...",
      confirmedTitle: "You're booked.",
      confirmedSentTo: "A confirmation has been sent to",
      yourEmail: "your email",
      another: "Book another time",
      errDate: "Please select a date and time.",
      errName: "Please enter your name and email.",
      errLoad: "Couldn't load availability. Please try again.",
      errFail: "Booking failed. Please try again.",
    },
    contact: {
      eyebrow: "Get In Touch",
      title1: "Let\u2019s build something",
      title2: "lasting.",
      subtitle: "Book a private strategy call. Tell us where you are and where you want to go \u2014 we\u2019ll map the path.",
      location: "Serving Maryland & entrepreneurs nationwide, USA",
      name: "Full Name", email: "Email", phone: "Phone", service: "Service", selectService: "Select a service",
      message: "How can we help?", messagePh: "Tell us about your goals...",
      general: "General Inquiry",
      send: "Send Message", sending: "Sending...",
      errFill: "Please fill in your name, email and message.",
      errFail: "Something went wrong. Please try again or reach us on WhatsApp.",
    },
    footer: {
      tagline: "Building businesses ready to grow. Formation, funding, credit and web design under one roof.",
      cta: "Book a Consultation",
      navigate: "Navigate", services: "Services",
      locations: "Popular Locations", resources: "Resources", blog: "Blog",
      serviceLinks: ["LLC Formation", "EIN Registration", "Business Funding", "Business Credit", "Credit Repair", "Website Design", "Consulting"],
      rights: "All rights reserved.",
      slogan: "Building Businesses Ready to Grow.",
    },
    whatsapp: "Hi Alexa Financial, I'd like to learn more about your services.",
  },

  es: {
    brand: { name: "Alexa Financial", suffix: "Multiservices" },
    nav: {
      services: "Servicios", process: "Proceso", websites: "Sitios Web",
      pricing: "Precios", about: "Nosotros", faq: "Preguntas",
      cta: "Agendar Llamada",
      blog: "Blog",
    },
    hero: {
      badge: "Tu socio integral de negocios y finanzas",
      title1: "Construyendo Negocios",
      title2: "Listos para Crecer.",
      subtitle: "Ayudamos a emprendedores a crear LLCs, obtener EIN, desarrollar crédito empresarial, acceder a financiamiento, diseñar páginas web profesionales, reparar crédito y hacer crecer sus negocios con confianza.",
      primary: "Agendar Consulta",
      secondary: "Ver Servicios",
      scroll: "Desliza",
      trust: "La confianza de más de 2,500 emprendedores",
      based: "Maryland y todo EE. UU.",
      captionRole: "Fundadora y CEO",
    },
    marquee: ["FORMACIÓN LLC", "REGISTRO EIN", "FINANCIAMIENTO", "CRÉDITO EMPRESARIAL", "REPARACIÓN DE CRÉDITO", "DISEÑO WEB", "CONSULTORÍA"],
    stats: {
      tagline: "Tu socio estratégico para crear, financiar y hacer crecer tu negocio.",
      items: [
        { value: 2500, suffix: "+", label: "Negocios Lanzados" },
        { value: 48, suffix: "M+", prefix: "$", label: "Financiamiento Obtenido" },
        { value: 98, suffix: "%", label: "Satisfacción del Cliente" },
      ],
    },
    services: {
      eyebrow: "Lo Que Hacemos",
      title1: "Todo lo que tu negocio necesita,",
      title2: "en un solo lugar.",
      subtitle: "Desde la formación hasta el financiamiento y tu presencia en línea — con la precisión de una firma financiera privada en Maryland y en todo el país.",
      items: [
        { tag: "Base", title: "Formación de LLC", desc: "Registro estatal, agente registrado y acuerdo operativo — tu negocio, legalmente estructurado para crecer." },
        { tag: "Cumplimiento", title: "Registro de EIN", desc: "Número de Identificación Fiscal Federal rápido para abrir cuentas, contratar y operar con confianza." },
        { tag: "Capital", title: "Financiamiento Empresarial", desc: "Accede a capital con perfiles listos para prestamistas y estrategias alineadas a tu crecimiento." },
        { tag: "Apalancamiento", title: "Crédito Empresarial", desc: "Construye líneas de crédito y un perfil sólido para financiarte sin garantías personales." },
        { tag: "Restaurar", title: "Reparación de Crédito", desc: "Disputa, elimina y reconstruye — restaura tu crédito personal con un marco comprobado." },
        { tag: "Presencia", title: "Diseño Web Profesional", desc: "Sitios web personalizados enfocados en conversión que posicionan tu marca como líder del mercado." },
        { tag: "Lanzamiento", title: "Sitios de Una Página", desc: "Una landing page rápida y elegante para poner tu negocio en línea y generar clientes hoy." },
        { tag: "Crecimiento", title: "Consultoría de Negocios", desc: "Asesoría personalizada para trazar tu camino desde la formación hasta la expansión con confianza." },
      ],
    },
    manifesto: {
      eyebrow: "Nuestra Filosofía",
      title1: "Los principios que",
      title2: "construyen imperios.",
      items: [
        { n: "01", title: "Estructura antes que escala", body: "Diseñamos la base legal y financiera para que tu ambición nunca supere tu infraestructura." },
        { n: "02", title: "Capital con intención", body: "El financiamiento es una herramienta, no un trofeo. Te posicionamos para el capital correcto, al costo correcto y en el momento correcto." },
        { n: "03", title: "El crédito como apalancamiento", body: "El crédito empresarial te separa de tu negocio, protege tus activos y multiplica lo que puedes construir." },
      ],
    },
    timeline: {
      eyebrow: "El Proceso",
      title1: "De la idea a",
      title2: "financiado y creciendo.",
      steps: [
        { step: "01", title: "Llamada de Descubrimiento", desc: "Trazamos tus metas, industria y tiempos en una sesión de estrategia privada." },
        { step: "02", title: "Formación de la Entidad", desc: "LLC registrada, EIN obtenido y documentos de cumplimiento entregados." },
        { step: "03", title: "Base de Crédito", desc: "Perfil de crédito empresarial establecido con líneas listas para prestamistas." },
        { step: "04", title: "Acceso a Financiamiento", desc: "Te posicionamos y aplicamos por capital alineado a tu plan de crecimiento." },
        { step: "05", title: "Escala y Asesoría", desc: "Consultoría continua y presencia web para crecer con confianza." },
      ],
      stepLabel: "Paso",
    },
    portfolio: {
      eyebrow: "Trabajo Destacado",
      title1: "Negocios que ayudamos a",
      title2: "construir.",
      items: [
        { cat: "LLC + Financiamiento", title: "Meridian Holdings" },
        { cat: "Diseño Web", title: "Atlas Web Presence" },
        { cat: "Crédito Empresarial", title: "Vantage Credit Build" },
        { cat: "Consultoría", title: "Noble & Co. Advisory" },
      ],
    },
    testimonials: {
      eyebrow: "Historias de Clientes",
      title1: "La confianza de",
      title2: "fundadores.",
      items: [
        { name: "Marcus R.", role: "Fundador, Empresa de Logística", quote: "Alexa Financial me llevó de una idea a una LLC financiada en semanas. El proceso se sintió como trabajar con una firma privada de patrimonio." },
        { name: "Danielle C.", role: "CEO, Marca de Belleza", quote: "Obtuve crédito empresarial sin garantía personal. Su estrategia es realmente de otro nivel." },
        { name: "Andre W.", role: "Dueño, Firma de Bienes Raíces", quote: "El equipo reconstruyó mi crédito y estructuró mi negocio impecablemente. Por fin siento control de mis finanzas." },
        { name: "Sofia M.", role: "Fundadora, Estudio de Diseño", quote: "Desde la marca hasta el financiamiento, se encargaron de todo. Mi negocio se ve y opera como una Fortune 500." },
      ],
    },
    pricing: {
      eyebrow: "Paquetes de LLC",
      title1: "Lanza tu negocio",
      title2: "de la forma correcta.",
      subtitle: "Paquetes de formación de LLC transparentes y todo incluido. Tarifa de registro estatal incluida en cada plan.",
      oneTime: "pago único",
      plans: [
        {
          name: "Plan Emprendedor", price: "$399.99", tagline: "Todo lo que necesitas para formar tu LLC.",
          features: ["Registro de LLC", "EIN (Número Fiscal Federal)", "Acuerdo Operativo", "Tarifa de Registro Estatal Incluida", "Consultoría de Negocios Personalizada"],
        },
        {
          name: "Plan Premium", price: "$599.99", tagline: "Formación más la base de tu marca y crédito.",
          features: ["Todo lo del Plan Emprendedor", "Sitio Web Profesional de Una Página", "Configuración de Perfil D-U-N-S\u00AE", "Revisión Inicial del Perfil de Crédito Empresarial", "Un Flyer Profesional para Redes Sociales", "Tarjeta de Presentación Digital", "Portada para Facebook", "Portada para WhatsApp Business", "Tarifa de Registro Estatal Incluida"],
        },
      ],
    },
    websites: {
      eyebrow: "Diseño Web Profesional",
      title1: "Un sitio web que",
      title2: "gana clientes.",
      subtitle: "Sitios web hechos a la medida, diseñados para generar confianza y convertir visitantes en clientes.",
      startingAt: "Desde",
      packages: [
        {
          name: "Landing Page", price: "$299.99", tagline: "Perfecto para negocios nuevos que quieren una presencia profesional en línea.",
          features: ["Landing Page Personalizada", "Diseño Responsivo para Móvil", "Formulario de Contacto", "Integración con WhatsApp", "SEO Básico", "Enlaces a Redes Sociales"],
        },
        {
          name: "Sitio Web de Negocios", price: "$499.99", tagline: "Perfecto para negocios que quieren construir credibilidad y atraer más clientes.",
          features: ["3\u20135 Páginas Personalizadas", "Diseño Profesional Único", "Responsivo para Móvil", "SEO Básico", "Integración con Google Maps", "Formularios de Contacto", "Integración con Redes Sociales"],
        },
        {
          name: "Sitio Web Premium", price: "$799.99", starting: true, tagline: "Perfecto para negocios que quieren vender en línea y competir con empresas más grandes.",
          features: ["Más de 5 Páginas", "Tienda en Línea (E-commerce)", "Integración de Pasarela de Pago", "Animaciones Avanzadas", "SEO Avanzado", "Diseño Premium Personalizado", "Blog Opcional", "Capacitación del Sitio Web"],
        },
      ],
      note: "Cada sitio web se diseña a la medida de tu negocio. Sin plantillas. Sin copiar y pegar. Tu negocio merece una presencia en línea única que genere confianza y atraiga más clientes.",
      getStarted: "Comenzar",
      contactUs: "Contáctanos",
    },
    about: {
      eyebrow: "Conoce a Tu Asesora",
      title1: "Guiado por",
      title2: "Alexa Guzman.",
      name: "Alexa Guzman",
      role: "Fundadora y CEO, Alexa Financial Multiservices",
      bio: "Alexa fundó Alexa Financial Multiservices para darles a los emprendedores las mismas ventajas que por mucho tiempo disfrutaron los adinerados — estructura adecuada, financiamiento real y una marca que inspira confianza. Con base en Maryland y atendiendo clientes en todo el país, ella guía personalmente cada negocio desde la formación hasta el crecimiento.",
      stat1: "Negocios guiados", stat1v: "2,500+",
      stat2: "Satisfacción del cliente", stat2v: "98%",
    },
    faq: {
      eyebrow: "Preguntas",
      title1: "Respuestas,",
      title2: "claras.",
      items: [
        { q: "¿Cuánto tarda en formarse mi LLC?", a: "La mayoría de las entidades se registran en 24\u201372 horas según tu estado. Hay opciones expeditas disponibles para estados selectos, incluyendo Maryland." },
        { q: "¿Realmente puedo obtener financiamiento sin garantía personal?", a: "Sí. Una vez que tu perfil de crédito empresarial y líneas de crédito están establecidos, te posicionamos para financiamiento que depende de tu negocio, no de tu crédito personal." },
        { q: "¿Trabajan con clientes fuera de Maryland?", a: "Por supuesto. Estamos en Maryland y atendemos emprendedores en todo el país, adaptando los registros y la estrategia a tu estado." },
        { q: "¿El diseño web está incluido en los planes de LLC?", a: "El Plan Premium de LLC incluye un sitio web profesional de una página. Los sitios completos de varias páginas y e-commerce se ofrecen por separado en nuestros paquetes de Diseño Web." },
        { q: "¿Mi información está segura?", a: "Sí. Usamos protocolos estrictos de confidencialidad y sistemas seguros para proteger tus datos en cada paso." },
      ],
    },
    booking: {
      eyebrow: "Agenda una Consulta",
      title1: "Reserva tu llamada",
      title2: "de estrategia privada.",
      subtitle: "Elige la fecha y hora que te convenga. Una asesora dedicada de Alexa Financial confirmará y enviará el enlace de tu llamada.",
      selectDate: "Selecciona una fecha",
      selectDateFirst: "Selecciona una fecha primero",
      availableTimes: "Horarios disponibles",
      chooseDate: "Elige una fecha para ver los horarios disponibles.",
      loading: "Cargando horarios...",
      name: "Nombre completo", email: "Correo electrónico", phone: "Teléfono (opcional)",
      service: "Servicio de interés", notes: "¿Algo que debamos saber? (opcional)",
      general: "Consulta General",
      confirm: "Confirmar Cita", booking: "Reservando...",
      confirmedTitle: "¡Tu cita está reservada!",
      confirmedSentTo: "Se envió una confirmación a",
      yourEmail: "tu correo",
      another: "Agendar otro horario",
      errDate: "Por favor selecciona una fecha y hora.",
      errName: "Por favor ingresa tu nombre y correo.",
      errLoad: "No se pudo cargar la disponibilidad. Intenta de nuevo.",
      errFail: "La reserva falló. Intenta de nuevo.",
    },
    contact: {
      eyebrow: "Contáctanos",
      title1: "Construyamos algo",
      title2: "duradero.",
      subtitle: "Agenda una llamada de estrategia privada. Cuéntanos dónde estás y a dónde quieres llegar — trazaremos el camino.",
      location: "Atendiendo Maryland y emprendedores en todo EE. UU.",
      name: "Nombre Completo", email: "Correo", phone: "Teléfono", service: "Servicio", selectService: "Selecciona un servicio",
      message: "¿Cómo podemos ayudarte?", messagePh: "Cuéntanos sobre tus metas...",
      general: "Consulta General",
      send: "Enviar Mensaje", sending: "Enviando...",
      errFill: "Por favor completa tu nombre, correo y mensaje.",
      errFail: "Algo salió mal. Intenta de nuevo o escríbenos por WhatsApp.",
    },
    footer: {
      tagline: "Construyendo negocios listos para crecer. Formación, financiamiento, crédito y diseño web en un solo lugar.",
      cta: "Agendar Consulta",
      navigate: "Navegar", services: "Servicios",
      locations: "Ubicaciones Populares", resources: "Recursos", blog: "Blog",
      serviceLinks: ["Formación de LLC", "Registro de EIN", "Financiamiento", "Crédito Empresarial", "Reparación de Crédito", "Diseño Web", "Consultoría"],
      rights: "Todos los derechos reservados.",
      slogan: "Construyendo Negocios Listos para Crecer.",
    },
    whatsapp: "Hola Alexa Financial, me gustaría conocer más sobre sus servicios.",
  },
};
